import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AssignmentReturnOutlined, AccessTimeOutlined, GavelOutlined, CancelOutlined, CurrencyRupeeOutlined, VerifiedUserOutlined, ArrowForward, StorefrontOutlined, SentimentSatisfiedAltOutlined, ErrorOutlineOutlined, CheckCircleOutline } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const ReturnExchangePolicyPage = () => {
    useEffect(() => {
        document.title = "Return & Exchange Policy | MAB Jewels Jewellery Store";

        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Read MAB Jewels return and exchange policy for gold jewellery. Clear guidelines on eligibility, timelines, and conditions.");
        }
    }, []);

    return (
        <div className="page-container seo-page">
            <div className="seo-content-wrapper">
                <h1>Return & Exchange Policy – MAB Jewels</h1>

                <p className="seo-intro">
                    At <strong>MAB Jewels</strong>, customer satisfaction is our priority. We follow transparent and fair return and exchange policies while maintaining the highest standards of jewellery quality, purity, and craftsmanship.
                </p>
                <p>Please read our policy carefully before making a purchase.</p>

                <h2>Eligibility for Return or Exchange</h2>
                <div style={{ background: '#f0f4c3', padding: '25px', borderRadius: '12px', border: '1px solid #dce775', marginBottom: '30px' }}>
                    <p style={{ marginTop: 0, fontWeight: 'bold', color: '#33691e', display: 'flex', alignItems: 'center' }}>
                        <CheckCircleOutline sx={{ mr: 1 }} /> Returns or exchanges are accepted only if all conditions below are met:
                    </p>
                    <ul className="seo-check-list" style={{ marginTop: '10px' }}>
                        <li><AssignmentReturnOutlined sx={{ color: '#558b2f', mr: 1, verticalAlign: 'middle' }} /> Jewellery is unused and in original condition</li>
                        <li><AssignmentReturnOutlined sx={{ color: '#558b2f', mr: 1, verticalAlign: 'middle' }} /> Original invoice/bill is presented</li>
                        <li><AssignmentReturnOutlined sx={{ color: '#558b2f', mr: 1, verticalAlign: 'middle' }} /> BIS hallmark remains intact</li>
                        <li><AssignmentReturnOutlined sx={{ color: '#558b2f', mr: 1, verticalAlign: 'middle' }} /> Request raised within the eligible timeframe</li>
                    </ul>
                </div>

                <h2>Timeframe for Return / Exchange</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                    <div style={{ background: '#fff3e0', padding: '20px', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                        <h3 style={{ marginTop: 0, color: '#e65100', fontSize: '1.1rem', display: 'flex', alignItems: 'center' }}>
                            <AccessTimeOutlined sx={{ mr: 1 }} /> Gold Jewellery
                        </h3>
                        <p style={{ marginBottom: 0 }}>Exchange allowed within <strong>7 days</strong> of purchase.</p>
                    </div>
                    <div style={{ background: '#e0f7fa', padding: '20px', borderRadius: '8px', border: '1px solid #b2ebf2' }}>
                        <h3 style={{ marginTop: 0, color: '#006064', fontSize: '1.1rem', display: 'flex', alignItems: 'center' }}>
                            <AccessTimeOutlined sx={{ mr: 1 }} /> Silver Jewellery
                        </h3>
                        <p style={{ marginBottom: 0 }}>Exchange allowed within <strong>7 days</strong>.</p>
                    </div>
                </div>

                <div className="seo-highlight-box" style={{ background: '#ffebee', borderLeft: '4px solid #ef5350' }}>
                    <p style={{ color: '#c62828', margin: 0 }}>
                        <CancelOutlined sx={{ verticalAlign: 'middle', mr: 1 }} /> <strong>Customized / Made-to-order Jewellery:</strong> ❌ Not eligible
                    </p>
                </div>
                <p style={{ textAlign: 'center', fontWeight: 'bold', marginTop: '15px' }}>⛔ No cash refunds are provided. Only exchange or store credit.</p>

                <h2>Exchange Conditions</h2>
                <ul className="seo-check-list">
                    <li><GavelOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Exchange value is calculated based on current gold rate</li>
                    <li><GavelOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Making charges are non-refundable</li>
                    <li><GavelOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Any price difference must be paid by the customer</li>
                </ul>

                <h2>Non-Returnable Items</h2>
                <p>The following items cannot be returned or exchanged:</p>
                <div className="seo-simple-list">
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ marginBottom: '8px', color: '#d32f2f' }}>❌ Customized jewellery</li>
                        <li style={{ marginBottom: '8px', color: '#d32f2f' }}>❌ Engraved items</li>
                        <li style={{ marginBottom: '8px', color: '#d32f2f' }}>❌ Jewellery damaged after purchase</li>
                        <li style={{ marginBottom: '8px', color: '#d32f2f' }}>❌ Jewellery without original invoice</li>
                    </ul>
                </div>

                <h2>Jewellery Buyback Policy (If Applicable)</h2>
                <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #eee' }}>
                    <p style={{ marginTop: 0 }}>Buyback (if offered) is subject to:</p>
                    <ul className="seo-check-list" style={{ marginTop: '10px' }}>
                        <li><CurrencyRupeeOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Current market gold rate</li>
                        <li><CurrencyRupeeOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Deduction of making charges</li>
                        <li><CurrencyRupeeOutlined sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Physical inspection</li>
                    </ul>
                    <p style={{ marginBottom: 0, fontStyle: 'italic', fontSize: '0.9rem' }}>Please contact our store for buyback eligibility.</p>
                </div>

                <h2>Exchange Process – Step by Step</h2>
                <div className="seo-steps-list">
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <strong style={{ background: '#d4af37', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '15px' }}>1</strong>
                        <span>Visit the MAB Jewels store</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <strong style={{ background: '#d4af37', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '15px' }}>2</strong>
                        <span>Carry original invoice</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <strong style={{ background: '#d4af37', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '15px' }}>3</strong>
                        <span>Jewellery inspection by our expert team</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <strong style={{ background: '#d4af37', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '15px' }}>4</strong>
                        <span>Exchange value calculated</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <strong style={{ background: '#d4af37', color: 'white', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '15px' }}>5</strong>
                        <span>New jewellery selection</span>
                    </div>
                </div>

                <h2>Important Notes</h2>
                <div className="seo-highlight-box">
                    <p style={{ margin: '0 0 8px 0' }}><ErrorOutlineOutlined sx={{ color: '#f57c00', verticalAlign: 'middle', mr: 1 }} /> Jewellery once exchanged cannot be returned again</p>
                    <p style={{ margin: '0 0 8px 0' }}><ErrorOutlineOutlined sx={{ color: '#f57c00', verticalAlign: 'middle', mr: 1 }} /> Prices fluctuate daily based on market conditions</p>
                    <p style={{ margin: 0 }}><ErrorOutlineOutlined sx={{ color: '#f57c00', verticalAlign: 'middle', mr: 1 }} /> Policy may change without prior notice</p>
                </div>

                <h2>Why Shop with Confidence at MAB Jewels?</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '20px' }}>
                    <div style={{ textAlign: 'center', padding: '15px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <VerifiedUserOutlined sx={{ fontSize: 40, color: '#d4af37', marginBottom: '10px' }} />
                        <p style={{ fontWeight: 'bold' }}>BIS Hallmark Certified Jewellery</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <CurrencyRupeeOutlined sx={{ fontSize: 40, color: '#d4af37', marginBottom: '10px' }} />
                        <p style={{ fontWeight: 'bold' }}>Transparent pricing</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <StorefrontOutlined sx={{ fontSize: 40, color: '#d4af37', marginBottom: '10px' }} />
                        <p style={{ fontWeight: 'bold' }}>Trusted legacy of Manoj Abhushan Bhandar</p>
                    </div>
                    <div style={{ textAlign: 'center', padding: '15px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <SentimentSatisfiedAltOutlined sx={{ fontSize: 40, color: '#d4af37', marginBottom: '10px' }} />
                        <p style={{ fontWeight: 'bold' }}>Ethical and customer-first practices</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/bis-hallmarking-guide">BIS Hallmarking Guide</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/jewellery-care-guide">Jewellery Care Guide</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/trusted-jewellery-store-jodhpur">Trusted Jewellery Store in Jodhpur</Link></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ReturnExchangePolicyPage;
